import React, { useEffect, useState } from 'react';
import axios from 'axios';
import fileIcon from "../../assets/icons/file.svg";
import { baseUrl } from '../../constants/Constant';

const SearchIcon = () => (
    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);

const DownloadIcon = () => (
    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
);

const SkeletonCard = () => (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 animate-pulse">
        <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-gray-200 rounded-lg flex-shrink-0"></div>
            <div className="flex-1 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
        </div>
    </div>
);

function Documents() {
    const [docData, setDocData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        const fetchDocs = async () => {
            try {
                const res = await axios.get(`${baseUrl}/docs`);
                setDocData(res.data);
            } catch (error) {
                console.error("Error fetching documents:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDocs();
    }, []);

    const filteredDocs = docData.filter(item =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-50/50">
            <div className="md:container mx-auto px-[3%] md:px-[0] py-12 md:py-20 lg:py-24">

                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-8">
                    <div className="max-w-2xl">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-bold tracking-wider uppercase mb-6 border border-blue-100/50">
                            Hujjatlar
                        </span>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-gray-900 tracking-tight">
                            Bizning <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Hujjatlarimiz</span>
                        </h1>
                        <p className="text-gray-500 text-lg md:text-xl leading-relaxed">
                            Kompaniya faoliyatiga oid barcha rasmiy hujjatlar, nizomlar va hisobotlarning elektron to'plami bilan tanishing.
                        </p>
                    </div>

                    {/* Search Input */}
                    <div className="w-full md:w-80 lg:w-96 relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <SearchIcon />
                        </div>
                        <input
                            type="text"
                            className="w-full pl-11 pr-4 py-3.5 bg-white border-none ring-1 ring-gray-200 rounded-2xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 shadow-sm transition-all duration-200 hover:ring-gray-300"
                            placeholder="Hujjat nomini qidirish..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Content Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {loading ? (
                        [...Array(6)].map((_, i) => <SkeletonCard key={i} />)
                    ) : filteredDocs.length > 0 ? (
                        filteredDocs.map((item, index) => (
                            <a
                                href={item.document}
                                target="_blank"
                                rel="noopener noreferrer"
                                key={index}
                                className="group bg-white p-6 rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_24px_rgba(0,0,0,0.08)] border border-gray-100 transition-all duration-300 hover:-translate-y-1 block"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="p-3 bg-blue-50 rounded-xl group-hover:bg-blue-100 transition-colors duration-300">
                                        <img className="w-8 h-8 md:w-10 md:h-10" src={fileIcon} alt="File" />
                                    </div>
                                    <div className="opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300">
                                        <div className="p-2 bg-gray-50 rounded-lg text-gray-400 group-hover:text-blue-600">
                                            <DownloadIcon />
                                        </div>
                                    </div>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-700 transition-colors">
                                    {item.name}
                                </h3>
                                <div className="flex items-center text-sm text-gray-500">
                                    <span className="font-medium">{item.size || 'PDF'}</span>
                                    <span className="mx-2">•</span>
                                    <span>Yuklab olish</span>
                                </div>
                            </a>
                        ))
                    ) : (
                        <div className="col-span-full py-12 text-center">
                            <div className="inline-flex p-4 rounded-full bg-gray-50 mb-4">
                                <SearchIcon />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900">Hujjat topilmadi</h3>
                            <p className="text-gray-500 mt-1">So'rovingiz bo'yicha hech narsa topilmadi. Qaytadan urinib ko'ring.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default Documents
